// architecture.ts

import type {
  Cmd,
  ImmutableResult,
  InitialProperties,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  makeVariants,
  Variant,
} from '#lit/components/mixins/immutable_component/index.js';
import type {FeatureFlagsManager} from '#lit/lib/feature_flags/FeatureFlagsManager.js';
import {featureFlagsManager} from '#lit/lib/feature_flags/FeatureFlagsManager.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import type {ApplicationFeatureFlags} from '#lit/types/feature_flags.js';
import type {Nullable} from '#lit/types/general.js';
import type {Organization, Team} from '#lit/types/teams.js';
import type {User} from '#lit/types/user.js';
import {sessionStore} from '#stores/session/index.js';

import type {SessionProvider} from './index.js';

export type Model = {
  user: Nullable<User>;
  organization: Nullable<Organization>;
  team: Nullable<Team>;
  mixpanelProjectId: Nullable<string>;
  mixpanelDistinctId: Nullable<string>;
  embed: boolean;
  flags: FeatureFlagsManager;
};

export const Msg = makeVariants({
  setUser: Variant<Model['user']>()('set-user'),
  setOrg: Variant<Model['organization']>()('set-org'),
  setTeam: Variant<Model['team']>()('set-team'),
});
export type Msg = VariantsFor<typeof Msg>;

function setProvidedUser(user: Nullable<User>): Nullable<User> {
  const userCopy = Object.assign(Object.create(null), user);
  sessionStore.getState().setUser(userCopy);
  return Object.freeze(userCopy);
}

function setProvidedOrg(org: Nullable<Organization>): Nullable<Organization> {
  const orgCopy = Object.assign(Object.create(null), org);
  sessionStore.getState().setOrg(orgCopy);
  return Object.freeze(orgCopy);
}

function setProvidedTeam(team: Nullable<Team>): Nullable<Team> {
  const teamCopy = Object.assign(Object.create(null), team);
  sessionStore.getState().setTeam(teamCopy);
  return Object.freeze(teamCopy);
}

function setProvidedMixpanelProjectId(
  mixpanelProjectId: Nullable<string>
): Nullable<string> {
  sessionStore.getState().setMixpanelProjectId(mixpanelProjectId);
  return mixpanelProjectId;
}

function setProvidedMixpanelDistinctId(
  mixpanelDistinctId: Nullable<string>
): Nullable<string> {
  sessionStore.getState().setMixpanelDistinctId(mixpanelDistinctId);
  return mixpanelDistinctId;
}

function init(props: InitialProperties, cmd: Cmd): ImmutableResult<Model> {
  const flags = featureFlagsManager.setupFlags(
    <ApplicationFeatureFlags>props.get('flags') ?? {}
  );
  sessionStore.getState().setFlags(flags);

  return [
    {
      organization: setProvidedOrg(
        <Organization>props.get('organization') ?? null
      ),
      team: setProvidedTeam(<Team>props.get('team') ?? null),
      user: setProvidedUser(<User>props.get('user') ?? null),
      mixpanelProjectId: setProvidedMixpanelProjectId(
        (<string>props.get('mixpanelProjectId'))?.trim() || null
      ),
      mixpanelDistinctId: setProvidedMixpanelDistinctId(
        (<string>props.get('mixpanelDistinctId'))?.trim() || null
      ),
      embed: props.get('embed') === 'true',
      flags: featureFlagsManager,
    },
    cmd((host: SessionProvider) => {
      host.session.updateObservers();
    }),
  ];
}

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-org':
      return [
        {...model, organization: msg.value} as const,
        cmd((host: SessionProvider) => {
          host.session.updateObservers();
          sessionStore.getState().setOrg(host.model.organization);
        }),
      ];
    case 'set-team':
      return [
        {...model, team: msg.value} as const,
        cmd((host: SessionProvider) => {
          host.session.updateObservers();
          sessionStore.getState().setTeam(host.model.team);
        }),
      ];
    case 'set-user':
      return [
        {...model, user: msg.value} as const,
        cmd((host: SessionProvider) => {
          host.session.updateObservers();
          sessionStore.getState().setUser(host.model.user);
        }),
      ];
  }
}

export const main = {init, update};
